import { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Loader2, X, HeartPulse, MessageSquare, Phone, AlertTriangle, Flame } from "lucide-react";

const healthColor = (h) => (h >= 80 ? "text-emerald-400" : h >= 50 ? "text-amber-400" : "text-red-400");
const healthBar = (h) => (h >= 80 ? "bg-emerald-500" : h >= 50 ? "bg-amber-500" : "bg-red-500");

export default function ChipHealthModal({ chip, onClose }) {
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!chip) return;
    setMetrics(null);
    setError(null);
    api.get(`/chips/${chip.id}/metrics`)
      .then(({ data }) => setMetrics(data))
      .catch(() => setError("Falha ao carregar as métricas do chip."));
  }, [chip]);

  return (
    <Dialog open={!!chip} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900/95 backdrop-blur-md border-slate-800 text-slate-100 sm:max-w-lg" data-testid="chip-health-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-slate-100">
            Saúde do chip {chip?.apelido}
          </DialogTitle>
          <button
            onClick={onClose}
            data-testid="chip-health-close-button"
            className="absolute right-4 top-4 text-slate-500 hover:text-slate-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogHeader>

        {error ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-sm text-red-300" data-testid="chip-health-error">{error}</div>
        ) : !metrics ? (
          <div className="flex flex-col items-center py-12 gap-3">
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
            <p className="text-sm text-slate-400">Carregando métricas...</p>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center gap-4 bg-[#161F32] border border-slate-800 rounded-xl p-4">
              <HeartPulse className={`w-8 h-8 ${healthColor(metrics.health)}`} />
              <div className="flex-1">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Índice de saúde</p>
                <p className={`text-2xl font-bold font-mono ${healthColor(metrics.health)}`} data-testid="chip-health-value">{metrics.health}%</p>
              </div>
              <div className="w-32 bg-slate-800 rounded-full h-2 overflow-hidden">
                <div className={`${healthBar(metrics.health)} h-2 rounded-full transition-all duration-500`} style={{ width: `${metrics.health}%` }} />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-[#161F32] border border-slate-800 rounded-lg p-3 text-center" data-testid="chip-metric-today">
                <MessageSquare className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                <p className="text-lg font-bold font-mono text-slate-100">{metrics.messages_today}</p>
                <p className="text-[10px] text-slate-500 uppercase">msgs hoje</p>
              </div>
              <div className="bg-[#161F32] border border-slate-800 rounded-lg p-3 text-center" data-testid="chip-metric-total">
                <Flame className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                <p className="text-lg font-bold font-mono text-slate-100">{metrics.total_messages}</p>
                <p className="text-[10px] text-slate-500 uppercase">total</p>
              </div>
              <div className="bg-[#161F32] border border-slate-800 rounded-lg p-3 text-center" data-testid="chip-metric-calls">
                <Phone className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                <p className="text-lg font-bold font-mono text-slate-100">{metrics.calls_total}</p>
                <p className="text-[10px] text-slate-500 uppercase">chamadas</p>
              </div>
              <div className="bg-[#161F32] border border-slate-800 rounded-lg p-3 text-center" data-testid="chip-metric-errors">
                <AlertTriangle className="w-4 h-4 text-amber-400 mx-auto mb-1" />
                <p className="text-lg font-bold font-mono text-slate-100">{metrics.errors_7d}</p>
                <p className="text-[10px] text-slate-500 uppercase">erros 7d</p>
              </div>
            </div>

            <div className="bg-[#161F32] border border-slate-800 rounded-xl p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Mensagens nos últimos 7 dias</p>
              <div className="h-40" data-testid="chip-health-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={metrics.chart} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                    <XAxis dataKey="dia" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#94A3B8" }} />
                    <Area type="monotone" dataKey="mensagens" stroke="#10B981" fill="#10B981" fillOpacity={0.15} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 text-[11px] text-slate-500">
              <span className="px-2 py-1 rounded bg-slate-800/70 border border-slate-700 font-mono">
                {metrics.wa_mode === "real" ? "Evolution API real" : "simulação"}
              </span>
              <span className="px-2 py-1 rounded bg-slate-800/70 border border-slate-700 font-mono">
                delay {metrics.delay_min}s–{metrics.delay_max}s
              </span>
              <span className="px-2 py-1 rounded bg-slate-800/70 border border-slate-700 font-mono">
                bloco {metrics.msgs_in_block}/{metrics.block_target ?? "—"}
              </span>
              <span className={`px-2 py-1 rounded border font-mono ${metrics.warmup_enabled ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-slate-800/70 border-slate-700"}`}>
                {metrics.warmup_enabled ? "aquecendo" : "pausado"}
              </span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
